import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';

export const MAX_USER_ALERTS = 100;

export function parseAlertDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function isAlertRead(alertItem, userId) {
  return Boolean(userId && alertItem.readBy && alertItem.readBy[userId]);
}

export function isAlertDismissed(alertItem, userId) {
  return Boolean(userId && alertItem.dismissedBy && alertItem.dismissedBy[userId]);
}

export function isAlertTargeted(alertItem, userData) {
  if (!userData) return false;
  const role = userData.role;
  const companyId = userData.companyId;
  const type = alertItem.targetType || 'all';

  if (type === 'all') return true;
  if (type === 'company') {
    return alertItem.targetCompanyId && alertItem.targetCompanyId === companyId;
  }
  if (type === 'role') {
    return alertItem.targetRole && alertItem.targetRole === role;
  }
  if (type === 'company_role') {
    return (
      alertItem.targetCompanyId &&
      alertItem.targetRole &&
      alertItem.targetCompanyId === companyId &&
      alertItem.targetRole === role
    );
  }
  if (type === 'user') {
    const uid = userData.uid;
    return Boolean(
      uid &&
      alertItem.assignedToUid &&
      alertItem.assignedToUid === uid,
    );
  }
  return false;
}

export function isAlertActiveNow(alertItem) {
  if ((alertItem.status || 'active') !== 'active') return false;
  const now = Date.now();
  const startsAt = parseAlertDate(alertItem.startsAt);
  const expiresAt = parseAlertDate(alertItem.expiresAt);
  if (startsAt && startsAt.getTime() > now) return false;
  if (expiresAt && expiresAt.getTime() < now) return false;
  return true;
}

/**
 * Active alerts visible to this user, newest first (capped).
 * Uses filtered queries so Firestore security rules can enforce targeting.
 */
export async function fetchActiveAlertsForUser(db, userData) {
  if (!userData) return [];

  const queries = buildAlertQueriesForUser(db, userData);

  const snaps = await Promise.all(queries.map((q) => getDocs(q)));
  const merged = new Map();
  snaps.forEach((snap) => {
    snap.docs.forEach((d) => {
      merged.set(d.id, { id: d.id, ...d.data() });
    });
  });

  return [...merged.values()]
    .filter((row) => isAlertTargeted(row, userData))
    .filter(isAlertActiveNow)
    .sort((a, b) => {
      const ad = parseAlertDate(a.createdAt)?.getTime() || 0;
      const bd = parseAlertDate(b.createdAt)?.getTime() || 0;
      return bd - ad;
    })
    .slice(0, MAX_USER_ALERTS);
}

/**
 * Build Firestore queries for alerts visible to this user.
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} userData
 * @return {import('firebase/firestore').Query[]}
 */
export function buildAlertQueriesForUser(db, userData) {
  if (!userData) return [];
  const role = userData.role;
  const companyId = userData.companyId;

  const queries = [
    query(collection(db, 'alerts'), where('targetType', '==', 'all')),
  ];
  if (companyId) {
    queries.push(
      query(
        collection(db, 'alerts'),
        where('targetType', '==', 'company'),
        where('targetCompanyId', '==', companyId),
      ),
      query(
        collection(db, 'alerts'),
        where('targetType', '==', 'company_role'),
        where('targetCompanyId', '==', companyId),
        where('targetRole', '==', role),
      ),
    );
  }
  if (role) {
    queries.push(
      query(
        collection(db, 'alerts'),
        where('targetType', '==', 'role'),
        where('targetRole', '==', role),
      ),
    );
  }
  const uid = userData.uid;
  if (uid) {
    queries.push(
      query(
        collection(db, 'alerts'),
        where('targetType', '==', 'user'),
        where('assignedToUid', '==', uid),
      ),
    );
  }
  return queries;
}

/**
 * Merge alert query snapshots into a sorted, filtered list.
 * @param {Map<string, object>} merged
 * @param {object} userData
 * @return {object[]}
 */
export function mergeActiveAlerts(merged, userData) {
  return [...merged.values()]
      .filter((row) => isAlertTargeted(row, userData))
      .filter(isAlertActiveNow)
      .sort((a, b) => {
        const ad = parseAlertDate(a.createdAt)?.getTime() || 0;
        const bd = parseAlertDate(b.createdAt)?.getTime() || 0;
        return bd - ad;
      })
      .slice(0, MAX_USER_ALERTS);
}

/**
 * Subscribe to live alert updates for a user (multiple queries merged).
 * @param {import('firebase/firestore').Firestore} db
 * @param {object} userData
 * @param {(rows: object[]) => void} onUpdate
 * @param {(err: Error) => void} [onError]
 * @return {() => void}
 */
export function subscribeActiveAlertsForUser(db, userData, onUpdate, onError) {
  const queries = buildAlertQueriesForUser(db, userData);
  if (!queries.length) {
    onUpdate([]);
    return () => {};
  }

  const merged = new Map();
  const unsubs = queries.map((q) =>
    onSnapshot(
      q,
      (snap) => {
        snap.docs.forEach((d) => {
          merged.set(d.id, { id: d.id, ...d.data() });
        });
        onUpdate(mergeActiveAlerts(merged, userData));
      },
      (err) => {
        console.error('subscribeActiveAlertsForUser:', err);
        onError?.(err);
      },
    ),
  );

  return () => unsubs.forEach((unsub) => unsub());
}
